/**
 * BOSS Reports — collapsing a side-by-side grid to a row field, with group sums.
 *
 * The pivot-grid gesture every desktop user knows: collapse to STORENAME and see one row per store
 * with the measures summed, the fields to the right blank, and — as DevExpress does — a TOTAL row
 * plus a subtotal row for each level above. Rows stay side by side. Nothing leaves the Rows area.
 *
 * WHAT THE ENGINE CAN AND CANNOT DO (measured). In side-by-side mode (`group_rollup_mode: 'flat'`)
 * the engine returns leaf rows only and IGNORES depth: `set_depth(2)` was accepted and changed
 * nothing (422 leaves before and after). In tree mode (`'rollup'`) it honours depth and returns
 * exactly the rows wanted, with true group sums (depth 2 on the same report: 162 rows, QUANTITY
 * 1,566 for TOTAL). But the datagrid's tree formatter emits only each row's LAST path part — the
 * parents live on parent rows — while side by side needs every part on every row, which only the
 * FLAT formatter produces (one formatted cell per path part), and only for leaves.
 *
 * SO: "FLAT PLUGIN, ROLLUP VIEW". The viewer's config stays `'flat'` — the plugin keeps its
 * side-by-side formatter and its `flat-group-rollup-mode` class, and `save()` keeps saying flat —
 * while the plugin READS from a second View this module creates from the same Table with
 * `{ ...config, group_rollup_mode: 'rollup', group_by_depth: d }`. `table.view()` honours
 * `group_by_depth` (measured: 8 rows = TOTAL + 2 regions + 5 stores); `viewer.restore()` does not,
 * which is why the level cannot live in the Perspective config and is persisted in the layout
 * envelope beside the pin lists instead (`collapseToRowField`, by NAME — an index would silently
 * re-point when Rows are reordered).
 *
 * ONE GROUP AT A TIME. The tree view is behind EVERY side-by-side grid with a Rows area, and the
 * level only seeds its depth (null: the full depth, every leaf shown and no total row at all). The
 * view keeps per-node expansion: `toggleGroupAt` calls its `expand(row)` / `collapse(row)` for the
 * one group a [+] or [−] names (lib/bossRowToggles) — [+] opening the group's whole subtree, level
 * by level (`expandSubtree`) — and `setFieldOpen` for every visible group of one field (Expand All /
 * Collapse All, the corner glyphs) — measured on 5.3.1 in Node: `expand` on a
 * `group_by_depth` view adds exactly that node's children, `collapse` removes a subtree, the paths
 * keep pre-order, calls issued together settle in order, and a reopened parent's children come back
 * closed. The level changes only through those gestures, which announce it after the fact
 * (`markCollapseApplied`), or through a uniform `set_depth` when it arrives any other way; a config
 * change makes a new view and starts from the level. Nothing of the per-node state is persisted,
 * as the desktop grid persisted none of it (parity C10). The permutation reads ancestors off the
 * SHAPE (a row the next row is deeper than), so an opened group is a subtotal after its children
 * and a closed one a single leaf, and the columns drawn grow to the longest displayed path
 * (`RowOrder.shown`), which `visibleRowDepth` reports to the pin split, the captions, the cap and
 * the hit test.
 *
 * The swap is a Proxy over the model's `_view` that routes reads (`schema`, `column_paths`, …) to
 * the collapsed view — the windowed reads translated to display order and `num_rows` answered
 * with the DISPLAY count, see below — and `expand` / `collapse` / `set_depth` / `delete` to the
 * REAL flat view. That exception is load-bearing: the plugin routes any tree-label header click to its
 * expand/collapse handler with no flat-mode gate, and against the real view the engine ignores it,
 * so a click cannot desynchronise the drawn rows from the persisted level (measured).
 *
 * The flat formatter accepts a short path and yields fewer cells; the rest are padded here. The
 * blank is a fresh `{ toString: () => ' ' }` PER CELL, and every part of that matters:
 *  - an object, because regular-table merges row headers by `===` — colspan into an equal previous
 *    column, rowspan into an equal row above — and identity-unique objects never merge;
 *  - a non-breaking space, because an EMPTY th matches the plugin's ungated `tbody th:empty` rule
 *    (tree connector gradient, 20px cap, pointer-events none — a right-click falls through), while
 *    `trim()` of a nbsp is empty so the cell earns no `psp-tree-label`;
 *  - a subtotal row's OWN level is identity-unique, so the labelled cell stays its own row rather
 *    than being rowspanned into its children, while the columns left of it are continuations
 *    (`continues`) that the group above spans straight through; leaf rows keep plain strings, and
 *    the next stage out (lib/bossRowHeaderMerge) keys all three so equal siblings rowspan only
 *    within one parent group, exactly as the uncollapsed grid's do.
 *
 * TOTALS BELOW THEIR GROUP. The rollup view returns rows in PRE-order — TOTAL first, each subtotal
 * before its children — and the desktop grid drew them the other way round: each subtotal after its
 * last child, the grand TOTAL last. The plugin's data listener is WINDOWED: per draw it asks the
 * (proxied) view for `[y0, y1)` through `to_columns_string`, and stores what came back on the model
 * (`_ids`, `_last_window`, `_reverse_ids`, …) for its click, edit and selection handlers to index
 * by display row. So the reorder cannot be done by calling the listener several times and
 * stitching — that state would describe only the last call (measured) — and is done INSIDE the
 * Proxy instead: a POST-order permutation is built once per collapsed view from `__ROW_PATH__`
 * alone (`to_columns_string({ start_col: 0, end_col: 0 })` returns only the paths: 0.5 ms at 400
 * rows, 8 ms at 12,400, 67 ms at 69,700 — where the rollup view itself took 1,150 ms), and every
 * windowed read (`to_columns_string`, `to_columns`, `to_json`) has
 * its display window translated to the view rows it needs — sorted, merged into contiguous runs
 * when closer than `RUN_GAP`, one real call per run, stitched back in display order. A subtotal
 * whose whole group fits the window sits right before it in pre-order, so a window is one or two
 * runs; a single span fetch was rejected because TOTAL is view row 0 and the bottom window would
 * span the whole view. The listener still makes exactly one call per draw and its state comes
 * out in display coordinates. Measured: +0.2 ms per listener call; the ~65 ms frame is unchanged.
 * regular-table merges row headers UPWARD only (tbody.ts:120-154), so a subtotal below its children
 * is never merged into them — as long as the `uniq` wrapper on ancestor rows stays. One accepted
 * difference from the desktop grid follows: the parent value is drawn again on the first leaf after
 * each subtotal, because the subtotal's unique cell ends the rowspan. Copy and export are NOT
 * affected: the viewer hands the plugin's `render()` its own real view, never the model's. The
 * order is keyed on (collapsed view, its row count, depth, hidden levels) and rebuilt whenever the
 * config — and so the view — changes or a level's totals are toggled; BOSS tables are fully
 * populated before the viewer sees them (lib/perspective), which is what makes a row-count key
 * safe. Copy/export therefore also ignores hidden levels: it never sees the permutation.
 *
 * Composition: this wrapper sits INSIDE the pinning wrapper (lib/bossRowPinning), so the pin split
 * sees full-length rows exactly as it does today; the datetime zone pin runs in the outer wrapper
 * before the inner call and therefore covers this path too. Captions and the width cap are
 * untouched — `group_by` is still the full list and the plugin keeps the flat class on. The
 * shading of total rows (lib/bossTotalsShading) asks `rowKindAt` what a display row is.
 *
 * Proved against the real 5.3.1 packages: row counts and paths at every level, group sums, blank
 * inertness, no merges, th click inert, a non-prefix pinned set, split_by, sort, theme and settings
 * redraws, deep scroll, expand-all restoring the leaves — 42/42, zero console errors; the
 * permutation separately at depths 1-4 on 59 and 12,000 leaves — 49/49.
 */
import type { ViewerConfig } from './bossLayoutOps'

/** The envelope key, as the server validates and stores it. */
export const COLLAPSE_KEY = 'collapseToRowField'

/**
 * The envelope key for the row fields whose SUBTOTAL rows are switched off — the legacy grid's
 * per-field "Show Totals". A grid with a level shows one subtotal per open group with two or more
 * visible children, at every level — above the level, and below it once a group is opened; a group
 * with one child draws none, so a STORENUM level one-to-one with STORENAME no longer needs turning
 * off by hand. The setting is a list of hidden fields, so the default — everything shown, as the
 * desktop tool defaulted — needs no key. Hidden rows are simply left out of the display
 * permutation; the grand TOTAL row is not affected by it (it is left out only on a grid with no
 * level, which draws no total row at all).
 */
export const HIDDEN_TOTALS_KEY = 'hiddenTotalsRowFields'

/** A Perspective view window; only the row bounds matter here, the rest passes through. */
type ViewWindow = { start_row?: number; end_row?: number; [key: string]: unknown }

/** The minimum of Perspective's View this module calls. */
type View = {
    num_rows(): Promise<number>
    delete(): Promise<void>
    expand(row: number): Promise<number>
    collapse(row: number): Promise<number>
    set_depth(depth: number): Promise<void>
    to_columns_string(window?: ViewWindow): Promise<string>
    to_columns(window?: ViewWindow): Promise<Record<string, unknown[]>>
    to_json(window?: ViewWindow): Promise<Record<string, unknown>[]>
}

type Table = { view(config: Record<string, unknown>): Promise<View> }

/** What a DISPLAY row of a collapsed grid is. */
export type RowKind = 'leaf' | 'subtotal' | 'total'

/**
 * The display permutation of a collapsed view: `disp[y]` is the VIEW row drawn at display row y
 * (`M` of them — fewer than the view's `N` rows when some levels' subtotals are hidden), `len[v]`
 * the path length of view row v, `anc[v]` 1 where view row v has visible children (it is drawn as
 * a subtotal row after them), `depth` the collapse depth, and `shown` the row-header columns the
 * grid draws: the longest DISPLAYED path, never fewer than `depth` — more once a group is opened
 * below the level. `longest` is that path length on its own, and `openAt` / `leafAt` count the open
 * groups and the leaves at each path length, for the corner glyphs.
 */
export type RowOrder = {
    disp: Int32Array; len: Uint8Array; anc: Uint8Array; N: number; M: number; depth: number; shown: number
    longest: number; openAt: Uint32Array; leafAt: Uint32Array
}

const NO_HIDDEN: ReadonlySet<number> = new Set()

/** The view methods whose windows are translated to display order. Exactly these three. */
const TRANSLATED = new Set<PropertyKey>(['to_columns_string', 'to_columns', 'to_json'])
type TranslatedMethod = 'to_columns_string' | 'to_columns' | 'to_json'

/** View rows closer than this are fetched as one run rather than two calls. */
const RUN_GAP = 8

type DatagridModel = {
    _config: { group_by: string[]; group_rollup_mode?: string }
    _view: View
    _table: Table
    _num_rows: number
}

type DataResponse = { row_headers?: unknown[][]; [key: string]: unknown }
type DataListener = (x0: number, y0: number, x1: number, y1: number) => Promise<DataResponse>

type CollapseState = {
    field: string | null
    /** Row fields whose subtotal rows are switched off; read at draw time like `field`. */
    hiddenTotals: readonly string[]
    key: string | null
    /** What `order` was built for — depth and hidden levels — so a toggle rebuilds it. */
    orderKey: string | null
    /** The same two, as values, for the rebuild after a group is opened or closed. */
    orderDepth: number
    orderHidden: ReadonlySet<number>
    /**
     * The level the VIEW's expansion reflects: set at creation, and again when the page delivers a
     * level a gesture announced (`expectedField`), because that gesture opened or closed the groups
     * itself. A level that arrives any other way — a discarded edit, a saved layout re-applied — is
     * applied uniformly with `set_depth`. The page's delivery is what confirms it, not the
     * announcement: between the two the page still carries the old level, and a draw in that gap
     * must not read the mismatch as a level to apply.
     */
    viewField: string | null | undefined
    expectedField: string | null | undefined
    table: Table | null
    viewPromise: Promise<View> | null
    view: View | null
    real: View | null
    proxy: View | null
    /** The display permutation of `orderView`; null exactly when the rows on screen are not permuted. */
    order: RowOrder | null
    orderView: View | null
}

const stateByViewer = new WeakMap<Element, CollapseState>()

/** Method names that must keep reaching the REAL (flat) view. See the module note. */
const ROUTE_TO_REAL = new Set<PropertyKey>(['expand', 'collapse', 'set_depth', 'delete'])

const NBSP = ' '

/** A blank cell that renders as nothing, never merges, and is not `:empty`. Fresh per cell. */
const blank = (): unknown => ({ toString: () => NBSP })

/**
 * The word a subtotal row's own cell carries after the group's value — "VPO Nelson Total" — so a
 * total row reads as one instead of looking like another detail row. The desktop grid wrote the
 * summary's own name there ("Sum"); "Total" is used for all of them because the Values area can
 * hold an average or a count beside a sum, and it is the word already on the grand TOTAL row.
 */
const TOTAL_WORD = 'Total'

/** Excel's own name for a null group key, so a blank subtotal is not just the bare word. */
const BLANK_KEY = '(blank)'

/** A present value wrapped so it never rowspan/colspan-merges. The text is unchanged. */
const uniq = (value: unknown): unknown =>
    value instanceof HTMLElement ? value : { toString: () => String(value ?? '') }

/**
 * A subtotal row's cell in a column LEFT of the field it totals: the group above CONTINUES through
 * it. The row-header merge (lib/bossRowHeaderMerge) keys such a cell to the cell above, so
 * regular-table's rowspan swallows it and the parent is drawn once, unbroken — the grid reads
 * exactly as it does with no subtotal rows at all, save for the total line itself, and nothing of
 * the parent is repeated on that line. Before this the parts were `uniq`, which drew "VPO
 * Abbotsford" again on every one of its tills' total rows and split the store's own cell into a
 * run per till.
 *
 * The value is carried on the symbol so the merge can compare it against the plain cell above, and
 * so a consumer that cannot span — an unpinned row field, which the pin split re-emits as an
 * ordinary data column — can blank it instead of printing it.
 */
const CONTINUATION = Symbol('boss-row-continuation')

type Continuation = { [CONTINUATION]: unknown; toString(): string }

const continues = (value: unknown): Continuation => ({
    [CONTINUATION]: value ?? null,
    toString: () => String(value ?? ''),
})

/** The value a continuation cell carries; `undefined` for every other kind of cell. */
export function continuationValue(cell: unknown): unknown {
    return typeof cell === 'object' && cell !== null && CONTINUATION in cell
        ? (cell as Continuation)[CONTINUATION]
        : undefined
}

/**
 * A subtotal row's own cell: the group's value with the summary word beside it.
 *
 * Two things the plugin's formatter hands us that a template string would ruin:
 *  - an HTMLElement, which it returns for a string column carrying a `format` of bold, italics or
 *    link (data_listener/format_cell.ts), and which `String()` renders as "[object HTMLElement]".
 *    That is reachable today: the grid menu offers Format To on a row header, and the entry
 *    survives `restore()` and `save()` for a string row field (measured). The element is kept and
 *    the word appended beside it in a fresh span — regular-table appends an element cell value and
 *    compares cells by identity, so a per-cell span both renders and never merges.
 *  - `null`, which is what a NULL group key formats to. Named rather than left as a bare " Total",
 *    which would read like the grand total row sitting a few lines below it.
 */
function labelled(value: unknown): unknown {
    if (value instanceof HTMLElement) {
        const span = document.createElement('span')
        span.appendChild(value)
        span.appendChild(document.createTextNode(` ${TOTAL_WORD}`))
        return span
    }
    const text = String(value ?? '')
    return uniq(`${text.trim() === '' ? BLANK_KEY : text} ${TOTAL_WORD}`)
}

// ── State ─────────────────────────────────────────────────────────────

/**
 * Sets the field the grid collapses to, or null for fully expanded. Call before the first
 * `restore()` so the first paint is already collapsed; after that, redraw (`redrawForPinChange`).
 */
export function setRowCollapse(viewer: Element, field: string | null): void {
    const current = stateByViewer.get(viewer)
    if (current) {
        // The level a gesture announced, now delivered: the view already reflects it.
        if (current.expectedField !== undefined && field === current.expectedField) {
            current.viewField = field
            current.expectedField = undefined
        }
        current.field = field
        return
    }
    stateByViewer.set(viewer, freshState(field, []))
}

/**
 * Sets the row fields whose subtotal rows the next draw leaves out. Same lifecycle as
 * `setRowCollapse`: before the first `restore()`, then a redraw. Fields that are not row fields, or
 * the last row field, have no subtotal rows and are ignored at draw time; any other field can have
 * them once a group above it is open.
 */
export function setHiddenTotals(viewer: Element, hiddenTotals: readonly string[]): void {
    const current = stateByViewer.get(viewer)
    if (current) {
        current.hiddenTotals = hiddenTotals
        return
    }
    stateByViewer.set(viewer, freshState(null, hiddenTotals))
}

function freshState(field: string | null, hiddenTotals: readonly string[]): CollapseState {
    return {
        field, hiddenTotals, key: null, orderKey: null, orderDepth: 0, orderHidden: NO_HIDDEN, viewField: undefined,
        expectedField: undefined, table: null, viewPromise: null, view: null,
        real: null, proxy: null, order: null, orderView: null,
    }
}

/**
 * Releases the collapsed view and forgets the viewer. MUST run in the adapter's teardown before the
 * element is deleted: the table's `delete({ lazy: true })` waits for every view over it, so a view
 * left behind pins the whole result set in WASM memory for the life of the page.
 */
export function clearRowCollapse(viewer: Element): void {
    const state = stateByViewer.get(viewer)
    if (!state) return
    releaseView(state)
    stateByViewer.delete(viewer)
}

/** The collapsed view, for a consumer that must see the rows on screen; `viewer.getView()` shows leaves. */
export function collapsedViewOf(viewer: Element): View | null {
    return stateByViewer.get(viewer)?.view ?? null
}

/** Fire-and-forget: the view is ours, and its creation promise may not have settled yet. */
function releaseView(state: CollapseState): void {
    const old = state.viewPromise
    state.viewPromise = null
    state.view = null
    state.key = null
    state.order = null
    state.orderView = null
    state.orderKey = null
    state.viewField = undefined
    state.expectedField = undefined
    if (old) old.then(view => view.delete()).catch(() => undefined)
}

/**
 * A stored or edited hidden-totals list, reconciled with the Rows area: only row fields, each once,
 * in group_by order so two equal settings compare equal whatever order they were toggled in.
 */
export function normaliseHiddenTotals(config: ViewerConfig, hidden: readonly unknown[] | null | undefined): string[] {
    const groupBy = config.group_by
    if (!Array.isArray(groupBy) || !Array.isArray(hidden)) return []
    const wanted = new Set(hidden.filter((name): name is string => typeof name === 'string'))
    return groupBy.filter((name): name is string => typeof name === 'string' && wanted.has(name))
}

/** Reads the hidden-totals list off a stored layout envelope; absent or garbage read as "all shown". */
export function hiddenTotalsOfEnvelope(envelope: unknown): string[] {
    if (typeof envelope !== 'object' || envelope === null || Array.isArray(envelope)) return []
    const value = (envelope as Record<string, unknown>)[HIDDEN_TOTALS_KEY]
    return Array.isArray(value) ? value.filter((name): name is string => typeof name === 'string') : []
}

/** Equality for two normalised lists. */
export function sameHiddenTotals(a: readonly string[], b: readonly string[]): boolean {
    return a.length === b.length && a.every((name, i) => name === b[i])
}

/**
 * What display row `y` of this viewer's grid is. 'leaf' for every row of an expanded grid, and for
 * any viewer this module does not know. Answered from the permutation, which exists exactly while
 * the rows on screen are permuted: it is built before the listener call of an active draw and
 * cleared before the listener call of the deactivating one.
 */
export function rowKindAt(viewer: Element, y: number): RowKind {
    return rowKindOf(stateByViewer.get(viewer)?.order ?? null, y)
}

/** `nodeGlyphOf` against the viewer's live order; null before the first draw. */
export function nodeGlyphAt(viewer: Element, y: number, level: number, fieldCount: number): NodeGlyph | null {
    return nodeGlyphOf(stateByViewer.get(viewer)?.order ?? null, y, level, fieldCount)
}

/** `fieldGlyphOf` against the viewer's live order; null before the first draw. */
export function fieldGlyphAt(viewer: Element, level: number, fieldCount: number): NodeGlyph | null {
    return fieldGlyphOf(stateByViewer.get(viewer)?.order ?? null, level, fieldCount)
}

/** A press on a group, resolved at the press: the order it was resolved against, the view row, the action. */
export type GroupTarget = { order: RowOrder; row: number; action: 'open' | 'close' }

/**
 * Resolves the cell at display row `y`, row field `level` to the group it names and what a press
 * does to it — SYNCHRONOUSLY, at the press, against the order the rows on screen were drawn from:
 * the leaf's own closed group (its last part) is opened, an open group above it (a part to the left)
 * is closed. The gesture itself runs later, queued behind earlier ones, and a gesture rebuilds the
 * order before its redraw, so a display row read from the DOM means something only against the
 * order of that moment; `toggleGroupAt` refuses a target whose order has since been rebuilt. Null
 * when the cell names no group: a total row, a blank tail, or no order yet.
 */
export function groupTargetAt(viewer: Element, y: number, level: number): GroupTarget | null {
    const order = stateByViewer.get(viewer)?.order ?? null
    if (order === null || y < 0 || y >= order.M) return null
    const v = order.disp[y]
    if (order.len[v] === 0 || order.anc[v] === 1) return null
    const own = order.len[v] - 1
    if (level < own) {
        const ancestor = ancestorRowOf(order, v, level + 1)
        return ancestor < 0 ? null : { order, row: ancestor, action: 'close' }
    }
    return level === own ? { order, row: v, action: 'open' } : null
}

/**
 * Opens or closes ONE group, the one a press resolved to (`groupTargetAt`): opened with everything
 * under it, down to the last of the `fieldCount` row fields, or closed to that field. The engine
 * does the work on the collapsed view (`expand` / `collapse` by VIEW row), and the display order is
 * rebuilt here, before the caller redraws, so the next draw of BOTH wrappers reads the new shape —
 * the pin split counts its columns before the collapse wrapper's own rebuild would run. Resolves
 * false when nothing changed: no collapsed grid, a target resolved against an order since rebuilt
 * (a second press before the first's redraw), or a view replaced under the await. Transient, as the
 * desktop grid's partial expansion was: a config change makes a new view and starts from the level.
 */
export async function toggleGroupAt(viewer: Element, target: GroupTarget, fieldCount: number): Promise<boolean> {
    const st = stateByViewer.get(viewer)
    if (!st || st.view === null || st.order !== target.order) return false
    const view = st.view
    if (target.action === 'close') await view.collapse(target.row)
    else await expandSubtree(view, target.row, fieldCount)
    if (st.view !== view) return false
    return rebuildOrder(st, view, st.orderDepth, st.orderHidden, st.orderKey ?? '')
}

/**
 * The view rows under `v` — its subtree: the rows after it that are deeper than it — that are
 * closed, meaning no visible child (the next row is not deeper) and not on the last row field,
 * deepest row first. Pure.
 */
export function closedUnder(lengths: ArrayLike<number>, v: number, fieldCount: number): number[] {
    const rootLength = lengths[v]
    const rows: number[] = []
    for (let i = v + 1; i < lengths.length && lengths[i] > rootLength; i++) {
        const d = lengths[i]
        if (d >= fieldCount) continue
        const next = i + 1 < lengths.length ? lengths[i + 1] : 0
        if (next <= d) rows.push(i)
    }
    return rows.reverse()
}

/**
 * Opens the group at view row `v` and everything under it, down to the last row field: the desktop
 * grid's [+] showed a group's whole subtree, its rows expanded by default, and a till opened one
 * product at a time is not the detail it was opened for. Level by level — open the row, read the
 * paths back, open every closed row under it, again until none is left — because a level's rows
 * only exist in the view once the level above is open, and the engine numbers rows by the current
 * shape. Each round's calls are issued together and settle in order (descending rows keep each
 * other valid); there are at most as many rounds as fields below `v`, and the bound is enforced.
 * Exported for the tests.
 */
export async function expandSubtree(view: View, v: number, fieldCount: number): Promise<void> {
    await view.expand(v)
    for (let round = 0; round < fieldCount; round++) {
        const lengths = await readLengths(view)
        const closed = closedUnder(lengths, v, fieldCount)
        if (closed.length === 0) return
        await Promise.all(closed.map(row => view.expand(row)))
    }
}

/** Every row's path length, in view order. Paths only: `end_col: 0` returns nothing else. */
async function readLengths(view: View): Promise<number[]> {
    const parsed = JSON.parse(await view.to_columns_string({ start_col: 0, end_col: 0 })) as { __ROW_PATH__?: unknown[][] }
    return (parsed.__ROW_PATH__ ?? []).map(path => path.length)
}

/**
 * Tells the wrapper the view already reflects `field` as its level — the gesture that changed the
 * level opened or closed the groups itself — so the level's arrival from the page is not applied
 * again with `set_depth`, which would reopen every group closed above it. Call BEFORE announcing
 * the level to the page; it takes effect when the page delivers it (`setRowCollapse`).
 */
export function markCollapseApplied(viewer: Element, field: string | null): void {
    const st = stateByViewer.get(viewer)
    if (st) st.expectedField = field
}

/**
 * The view rows of the groups of row field `level` that `open` would change — the closed ones to
 * open, the open ones to close — in DESCENDING order, so that each call leaves every earlier row
 * where it was. A group hidden under a closed parent is not in the view and is left alone, as the
 * desktop grid left it. The last row field's groups have nothing below them and are never opened.
 * Pure.
 */
export function fieldNodes(order: RowOrder, level: number, open: boolean, fieldCount: number): number[] {
    if (level < 0 || level >= fieldCount || (open && level >= fieldCount - 1)) return []
    const length = level + 1
    const want = open ? 0 : 1
    const rows: number[] = []
    for (let v = order.N - 1; v >= 0; v--) if (order.len[v] === length && order.anc[v] === want) rows.push(v)
    return rows
}

/**
 * The view rows a whole-field EXPAND still has to open: every closed row at or below the field's
 * own level, deepest first. Closed means no visible child — the next row is not deeper — and a row
 * on the last field is never closed, having nothing under it.
 *
 * At or BELOW, not only at: Expand All opens each of the field's groups and everything to the right
 * of them, so a group two levels down that was left closed opens with the rest. A group closed
 * ABOVE the field is not in this list and stays closed, and nor is anything under it, because a
 * closed group's descendants are not in the view at all. Pure.
 */
export function closedFrom(lengths: ArrayLike<number>, level: number, fieldCount: number): number[] {
    if (level < 0 || level >= fieldCount - 1) return []
    const rows: number[] = []
    for (let i = 0; i < lengths.length; i++) {
        const d = lengths[i]
        if (d <= level || d >= fieldCount) continue
        const next = i + 1 < lengths.length ? lengths[i + 1] : 0
        if (next <= d) rows.push(i)
    }
    return rows.reverse()
}

/**
 * The shortest and longest leaf paths on screen, or null with no leaf. Which `set_depth`, if any,
 * equals a whole-field open or close is `uniformDepth`'s question, answered from this. Pure.
 */
export function leafLengthRange(order: RowOrder): [number, number] | null {
    let min = Infinity
    let max = -Infinity
    for (let y = 0; y < order.M; y++) {
        const v = order.disp[y]
        if (order.len[v] === 0 || order.anc[v] === 1) continue
        const d = order.len[v]
        if (d < min) min = d
        if (d > max) max = d
    }
    return min === Infinity ? null : [min, max]
}

/**
 * The one `set_depth` that equals opening or closing every visible group of row field `level`, or
 * null when only per-group calls will do. `set_depth(d)` shows the paths of up to d + 1 parts
 * (measured; `group_by_depth` counts one differently). Closing: `set_depth(level)` closes the field
 * and leaves everything above open, so it is right whenever no group above the field is closed —
 * the shortest leaf is on the field's own level or deeper. Opening: `set_depth(level + 1)` opens the
 * field and closes everything below it, so additionally nothing may be open more than one level
 * below — the longest leaf is at most two levels down. Pure.
 */
export function uniformDepth(
    order: RowOrder, level: number, open: boolean, fieldCount: number,
): number | null {
    const range = leafLengthRange(order)
    // A group closed ABOVE the field must stay closed, and set_depth would reopen it.
    if (range === null || range[0] < level + 1) return null
    return open ? fieldCount - 1 : level
}

/** What a whole-field open or close did. */
export type FieldOutcome = 'changed' | 'unchanged' | 'replaced'

/**
 * Opens or closes EVERY visible group of one row field — the menu's Expand All / Collapse All and
 * the corner glyphs — leaving a parent closed ABOVE the field closed, and everything under such a
 * parent with it.
 *
 * Expanding goes all the way DOWN: the field's groups and every closed group to the right of them,
 * so the rows the gesture reveals are whole detail rows rather than one more column of groups. That
 * is what "Expand All" says, and it is what a row's own [+] already did for the one group it names.
 * Collapsing is the field's own groups and no more — everything to the right goes with them by
 * disappearing, and reopening the field brings them back closed, which is the engine's own rule.
 *
 * The calls of a round are issued together and settle in order, because the engine answers a
 * session's requests in sequence and descending rows keep each other valid; when one `set_depth`
 * says the same thing (`uniformDepth`), that is the one call made. 'unchanged' when there was
 * nothing to do; 'replaced' when the view was swapped under an await — a config change — so the
 * caller must not announce a level the new view never received. The level is the caller's to
 * announce.
 */
export async function setFieldOpen(viewer: Element, level: number, open: boolean, fieldCount: number): Promise<FieldOutcome> {
    const st = stateByViewer.get(viewer)
    if (!st || st.view === null || st.order === null) return 'unchanged'
    const view = st.view

    let rows = open
        ? closedFrom(st.order.len, level, fieldCount)
        : fieldNodes(st.order, level, false, fieldCount)
    if (rows.length === 0) return 'unchanged'

    const depth = uniformDepth(st.order, level, open, fieldCount)
    if (depth !== null) {
        await view.set_depth(depth)
    } else if (!open) {
        await Promise.all(rows.map(v => view.collapse(v)))
    } else {
        // Round by round, exactly as one group's subtree is opened (`expandSubtree`): a level's rows
        // only exist in the view once the level above is open, so what to open next cannot be known
        // until the paths are read back. At most one round per field below this one.
        for (let round = 0; round < fieldCount && rows.length > 0; round++) {
            await Promise.all(rows.map(v => view.expand(v)))
            if (st.view !== view) return 'replaced'
            rows = closedFrom(await readLengths(view), level, fieldCount)
        }
    }

    if (st.view !== view) return 'replaced'
    return (await rebuildOrder(st, view, st.orderDepth, st.orderHidden, st.orderKey ?? '')) ? 'changed' : 'replaced'
}

/**
 * Reads every path of the collapsed view once and builds the display order from them. False when
 * the view was released while the read was in flight: a stale order must not land.
 */
async function rebuildOrder(
    st: CollapseState, view: View, depth: number, hiddenLengths: ReadonlySet<number>, orderKey: string,
): Promise<boolean> {
    // Paths only: `end_col: 0` returns nothing but __ROW_PATH__ (measured, split_by included).
    const parsed = JSON.parse(await view.to_columns_string({ start_col: 0, end_col: 0 })) as { __ROW_PATH__?: unknown[][] }
    if (st.view !== view) return false
    st.order = buildRowOrder(parsed.__ROW_PATH__ ?? [], depth, hiddenLengths)
    st.orderView = view
    st.orderKey = orderKey
    st.orderDepth = depth
    st.orderHidden = hiddenLengths
    return true
}

// ── The display permutation ───────────────────────────────────────────

/**
 * POST-order over the PRE-order rows of a rollup view: children first, then their subtotal, the
 * grand TOTAL (empty path) last. A single pass with a stack of open ancestors: a row of length d
 * closes every open ancestor of length >= d (emits it), then either opens (an ancestor) or is
 * emitted (a leaf). An ancestor whose path LENGTH is in `hiddenLengths` — the level whose "Show
 * Totals" is off — is closed without being emitted, so it has no display row at all.
 *
 * What makes a row an ancestor is the SHAPE, not the collapse depth: in pre-order a row has visible
 * children exactly when the next row is deeper than it. That is what lets one group be opened below
 * the level (`toggleGroupAt`) or closed above it: an opened group becomes an ancestor with a
 * subtotal row after its children, a closed one becomes a leaf — one row, blank tail — and the
 * depth only floors `shown`, the number of row-header columns to draw.
 *
 * A group with ONE visible child gets no subtotal row: its child already says everything the total
 * would, and drawing it doubled every one-till store and every one-line product. The desktop grid
 * hid those too (`ShowTotalsForSingleValues`, off by default), which is also why a STORENUM level
 * that is one-to-one with STORENAME no longer needs its totals switched off by hand. The grand TOTAL
 * is never suppressed for having one child; it is left out only when its own length, 0, is hidden —
 * the grid with no level — and then an empty result's lone TOTAL row is left out too. Pure;
 * exported for the tests.
 */
export function buildRowOrder(
    paths: readonly (readonly unknown[])[], depth: number, hiddenLengths: ReadonlySet<number> = NO_HIDDEN,
): RowOrder {
    const N = paths.length
    const disp = new Int32Array(N)
    const len = new Uint8Array(N)
    const anc = new Uint8Array(N)
    const stack: number[] = []
    /** Direct visible children of each open ancestor, parallel to `stack`. */
    const kids: number[] = []
    const openAt = new Uint32Array(256)
    const leafAt = new Uint32Array(256)
    let k = 0
    let longest = 0
    const close = (): void => {
        const i = stack.pop() as number
        const children = kids.pop() as number
        if (!hiddenLengths.has(len[i]) && (children >= 2 || len[i] === 0)) disp[k++] = i
    }
    for (let i = 0; i < N; i++) {
        const d = Math.min(paths[i].length, 255)
        len[i] = d
        while (stack.length > 0 && len[stack[stack.length - 1]] >= d) close()
        // Pre-order: whatever is left open on the stack is this row's parent.
        if (kids.length > 0) kids[kids.length - 1]++
        if (i + 1 < N && paths[i + 1].length > d) {
            anc[i] = 1
            openAt[d]++
            stack.push(i)
            kids.push(0)
        } else if (d === 0 && hiddenLengths.has(0)) {
            // The lone TOTAL of an empty view, on a grid that draws no total row: nothing at all.
        } else {
            leafAt[d]++
            disp[k++] = i
            if (d > longest) longest = d
        }
    }
    while (stack.length > 0) close()
    return { disp: disp.subarray(0, k), len, anc, N, M: k, depth, shown: Math.max(depth, longest), longest, openAt, leafAt }
}

/**
 * The row-header cells for one row of a collapsed grid, `depth` of them — the columns drawn, which
 * is the longest displayed path (`RowOrder.shown`).
 *
 * A LEAF row is passed through as the plugin formatted it, its missing tail (a group closed above
 * the level, or one left closed while another is open) filled with blanks; which equal neighbours
 * rowspan is decided by the next stage out (lib/bossRowHeaderMerge), which keys leaves and subtotals
 * alike on their group_by prefix. A SUBTOTAL row — an ancestor, drawn after its children — carries
 * the label on its own level, the LAST present part, the column of the field being totalled ("VPO
 * Nelson Total", where the desktop grid put it); its tail is blanks; and the columns LEFT of it
 * continue the group above (`continues`) rather than repeating it, so the parent's cell spans the
 * total line unbroken and the grid reads as it does with no total rows at all. The grand TOTAL row
 * has no path and says so in the first column. The kind comes from the order, not from the row's
 * length: a group opened at the collapse level is a subtotal at what used to be a leaf's full length.
 *
 * Pure, and the reason the padding is not written inline: this is the shape the whole feature is
 * judged on.
 */
export function collapsedRowHeader(row: readonly unknown[], depth: number, kind: RowKind): unknown[] {
    const length = row.length
    const out = new Array<unknown>(depth)
    for (let i = 0; i < depth; i++) {
        if (i >= length) out[i] = blank()
        else if (kind === 'leaf') out[i] = row[i]
        else out[i] = i === length - 1 ? labelled(row[i]) : continues(row[i])
    }
    if (kind === 'total') out[0] = uniq('TOTAL')
    return out
}

/** The kind of DISPLAY row `y` under `order`; 'leaf' out of range or with no order. */
export function rowKindOf(order: RowOrder | null, y: number): RowKind {
    if (order === null || y < 0 || y >= order.M) return 'leaf'
    const v = order.disp[y]
    return order.len[v] === 0 ? 'total' : order.anc[v] === 1 ? 'subtotal' : 'leaf'
}

/** What the [+]/[−] glyph on a cell says: the group it names is open, closed, or it names none. */
export type NodeGlyph = 'expanded' | 'collapsed' | 'none'

/**
 * The glyph for the cell at display row `y` in the column of row field `level` (its index in
 * group_by), with `fieldCount` row fields in all. A leaf row's own group — the cell of its LAST
 * part — is closed and gets [+], unless it is the last row field and there is nothing below it; the
 * cells left of it name the open groups above it and get [−], as the desktop grid's did; a blank
 * tail, a subtotal and the TOTAL name nothing. Null with no order: the grid has no group tree, and
 * the level gestures apply instead. Pure.
 */
export function nodeGlyphOf(order: RowOrder | null, y: number, level: number, fieldCount: number): NodeGlyph | null {
    if (order === null) return null
    if (y < 0 || y >= order.M) return 'none'
    const v = order.disp[y]
    if (order.len[v] === 0 || order.anc[v] === 1) return 'none'
    const own = order.len[v] - 1
    if (level < own) return 'expanded'
    if (level === own) return level < fieldCount - 1 ? 'collapsed' : 'none'
    return 'none'
}

/**
 * The glyph for a CORNER — row field `level`'s caption: [−] when any visible group of the field is
 * open (Collapse All has work to do), [+] when none is but some are closed on a field that is not
 * the last (Expand All has), nothing otherwise. Null with no order. Pure.
 */
export function fieldGlyphOf(order: RowOrder | null, level: number, fieldCount: number): NodeGlyph | null {
    if (order === null) return null
    if (level < 0 || level >= fieldCount) return 'none'
    const length = level + 1
    if (order.openAt[length] > 0) return 'expanded'
    if (order.leafAt[length] > 0 && level < fieldCount - 1) return 'collapsed'
    return 'none'
}

/**
 * Where the level goes after every visible group of `field` is opened or closed — the desktop
 * grid's rule for its persisted "totally collapsed" field. Closing a field above the current level
 * makes it the level; closing the level's own field or one below leaves the level alone. Opening
 * the level's own field moves the level one field down, off the end to null; opening any other
 * field leaves it alone. `undefined` means unchanged. Pure.
 */
export function levelAfterFieldAll(
    fields: readonly string[], current: string | null, field: string, open: boolean,
): string | null | undefined {
    const level = fields.indexOf(field)
    if (level < 0) return undefined
    const currentLevel = current === null ? fields.length : fields.indexOf(current)
    if (!open) return level < currentLevel ? field : undefined
    if (level !== currentLevel) return undefined
    return fields[level + 1] ?? null
}

/**
 * The view row of the ancestor of view row `v` whose path has `length` parts. Pre-order puts an
 * ancestor before all of its descendants with nothing shallower between, so it is the nearest
 * earlier row of that length. -1 when `v` has none at that length. Pure.
 */
export function ancestorRowOf(order: RowOrder, v: number, length: number): number {
    if (length < 1 || length >= order.len[v]) return -1
    for (let i = v - 1; i >= 0; i--) if (order.len[i] === length) return i
    return -1
}

/**
 * The view rows a display window `[y0, y1)` needs, as ascending contiguous runs `[a, b)`, merging
 * runs closer than `gap` so a subtotal a few rows before its group costs no extra call.
 */
export function runsFor(order: RowOrder, y0: number, y1: number, gap = RUN_GAP): [number, number][] {
    const rows = Array.from(order.disp.subarray(y0, y1)).sort((a, b) => a - b)
    const runs: [number, number][] = []
    for (const v of rows) {
        const last = runs[runs.length - 1]
        if (last !== undefined && v - last[1] <= gap) last[1] = v + 1
        else runs.push([v, v + 1])
    }
    return runs
}

/**
 * One windowed read of the collapsed view in DISPLAY order: the real method is called once per run
 * and the parts are stitched — per column for the column shapes, per row for `to_json`. The string
 * shape is parsed and re-serialised; the object shape is stitched as it came, so a caller that
 * inspects value types sees the native ones. A window that is empty once clamped to the DISPLAY
 * count is answered by a read past the view's own end — never by the caller's window as given,
 * because with hidden levels a display row at or past M is still a real view row (measured: it
 * came back as a pre-order row of a hidden level) — and Perspective answers a start past the end
 * with the empty shape, never a throw.
 */
export async function fetchWindow(
    view: View, method: TranslatedMethod, window: ViewWindow, order: RowOrder,
): Promise<unknown> {
    const y0 = Math.max(0, Math.floor(window.start_row ?? 0))
    const y1 = Math.min(order.M, Math.floor(window.end_row ?? order.M))
    if (y1 <= y0) {
        return (view[method] as (w: ViewWindow) => Promise<unknown>)({ ...window, start_row: order.N, end_row: order.N })
    }

    const runs = runsFor(order, y0, y1)
    const parts = await Promise.all(runs.map(([a, b]) =>
        (view[method] as (w: ViewWindow) => Promise<unknown>)({ ...window, start_row: a, end_row: b })))

    // Display row i sits in run r at offset v - a; runs are ascending and few, so a scan is fine.
    const locate = (i: number): [number, number] => {
        const v = order.disp[y0 + i]
        let r = 0
        while (runs[r][1] <= v) r++
        return [r, v - runs[r][0]]
    }
    const n = y1 - y0

    if (method === 'to_json') {
        const rows = parts as Record<string, unknown>[][]
        const out: Record<string, unknown>[] = new Array(n)
        for (let i = 0; i < n; i++) {
            const [r, offset] = locate(i)
            out[i] = rows[r][offset]
        }
        return out
    }

    const columns = (method === 'to_columns_string'
        ? (parts as string[]).map(part => JSON.parse(part) as Record<string, unknown[]>)
        : parts) as Record<string, unknown[]>[]
    const out: Record<string, unknown[]> = {}
    for (const key of Object.keys(columns[0] ?? {})) out[key] = new Array(n)
    for (let i = 0; i < n; i++) {
        const [r, offset] = locate(i)
        const part = columns[r]
        for (const key of Object.keys(out)) out[key][i] = part[key]?.[offset]
    }
    return method === 'to_columns_string' ? JSON.stringify(out) : out
}

// ── Pure helpers for the page ─────────────────────────────────────────

/** A stored or edited level, reconciled with the Rows area it applies to: not a row field → null. */
export function normaliseRowCollapse(config: ViewerConfig, field: string | null | undefined): string | null {
    if (typeof field !== 'string' || field === '') return null
    const groupBy = config.group_by
    return Array.isArray(groupBy) && groupBy.includes(field) ? field : null
}

/** Reads the level off a stored layout envelope; absent, null or garbage read as expanded. */
export function collapseOfEnvelope(envelope: unknown): string | null {
    if (typeof envelope !== 'object' || envelope === null || Array.isArray(envelope)) return null
    const value = (envelope as Record<string, unknown>)[COLLAPSE_KEY]
    return typeof value === 'string' && value !== '' ? value : null
}

/** How many leading row fields a collapse to `field` leaves on screen: all of them when expanded. */
function depthOf(groupBy: readonly string[], field: string | null | undefined): number {
    if (typeof field !== 'string') return groupBy.length
    const index = groupBy.indexOf(field)
    return index < 0 ? groupBy.length : index + 1
}

/**
 * The row fields a collapsed side-by-side grid DRAWS: the ones up to and including the collapse
 * level, in group_by order. The fields to the right of the level are hidden outright — no column,
 * no caption — rather than drawn blank, which is what the desktop grid did and what reads as
 * "collapsed" to its users. Expanded, this is group_by itself. Every consumer of the pinned/unpinned
 * lists and of the row-header count — the pin split, the corner captions, the width cap and the
 * right-click hit test — filters through this or through `visibleRowFieldsOf`, its live form, which
 * also counts the columns a group opened below the level has added.
 */
export function visibleRowFields(groupBy: readonly string[], collapse: string | null | undefined): string[] {
    return groupBy.slice(0, depthOf(groupBy, collapse))
}

/**
 * The same, read from the live state for the pinning wrapper, which runs on every draw and has the
 * viewer, not the page's props. Everything when the viewer is unknown or expanded.
 */
export function visibleRowDepth(viewer: Element | null, groupBy: readonly string[]): number {
    const state = viewer ? stateByViewer.get(viewer) : undefined
    const depth = depthOf(groupBy, state?.field)
    // A group opened below the level adds its columns, and the order says how many. Only an order
    // built for THIS level counts: right after a level change the previous one is still on the state.
    const order = state?.order ?? null
    if (order !== null && state?.field && order.depth === depth) return Math.min(Math.max(order.shown, depth), groupBy.length)
    return depth
}

/** `visibleRowFields` from the live state, the columns a group opened below the level included. */
export function visibleRowFieldsOf(viewer: Element | null, groupBy: readonly string[]): string[] {
    return groupBy.slice(0, visibleRowDepth(viewer, groupBy))
}

// ── The wrapper ───────────────────────────────────────────────────────

export function wrapRowCollapse(
    inner: DataListener,
    // `object` rather than a model type: the pinning module declares its own (narrower) view of the
    // model, and this module needs `_view`/`_table`/`_num_rows` that one has no business knowing.
    plugin: (Element & { model?: object }) | null,
    viewer: Element | null,
): DataListener {
    return async function collapsed(x0, y0, x1, y1) {
        const model = plugin?.model as DatagridModel | undefined
        const state = viewer ? stateByViewer.get(viewer) : undefined
        const groupBy = model?._config.group_by ?? []
        // Every side-by-side grid with a Rows area has the tree view behind it, level or no level:
        // that is what lets [−] close one group in a fully expanded grid. The level is the depth the
        // view starts at; null is the full depth.
        const depth = depthOf(groupBy, state?.field)
        const active = !!model && !!state
            && model._config.group_rollup_mode === 'flat'
            && groupBy.length >= 1

        if (!active) {
            if (model && state) {
                if (state.proxy && model._view === state.proxy) {
                    // Hand back the real view AND the real row count. The listener answers
                    // `num_rows: this._num_rows`, which was overwritten while collapsed, and a bare
                    // draw runs neither createModel nor update — so without this the collapsed count
                    // would persist and the grid would stop rows short (found and fixed in the proof).
                    model._view = state.real as View
                    model._num_rows = await (state.real as View).num_rows()
                }
                if (state.viewPromise) releaseView(state)
                // Unconditionally, not only inside releaseView: "order is null" has to mean "the rows
                // on screen are not permuted" for the shading pass, whatever path got here.
                state.order = null
                state.orderView = null
            }
            return inner(x0, y0, x1, y1)
        }

        // `state` and `model` are non-null here.
        const st = state as CollapseState
        const m = model as DatagridModel

        // One collapsed view per (config, table). The key and the promise are set SYNCHRONOUSLY
        // before any await: a dimension call and a window call arrive together and must share it.
        // The level is NOT in the key: it only seeds the view's depth, and a later level reaches the
        // view through the gestures — which open or close the groups themselves, then announce it —
        // or, failing that, through `set_depth` below; never through a rebuild, which would reopen
        // every group the user had closed above the level.
        const cfg = { ...m._config, group_rollup_mode: 'rollup' }
        const key = JSON.stringify(cfg)
        if (st.key !== key || st.table !== m._table) {
            releaseView(st)
            st.key = key
            st.table = m._table
            st.viewField = st.field
            st.viewPromise = m._table.view({ ...cfg, group_by_depth: depth })
        }
        const collapsedView = await (st.viewPromise as Promise<View>)
        if (st.viewField !== st.field) {
            // A level no gesture announced: applied uniformly. `set_depth(d)` shows the paths of up
            // to d + 1 parts (measured), where `group_by_depth: d` shows up to d.
            st.viewField = st.field
            await collapsedView.set_depth(depth - 1)
            st.order = null
        }
        st.view = collapsedView

        // Every activation builds a NEW model with a raw _view, so the proxy is re-installed per
        // model. Methods are bound to the target, not the proxy: the client's View class must see
        // its own `this`.
        if (m._view !== st.proxy) {
            st.real = m._view
            const real = st.real
            st.proxy = new Proxy(real as object, {
                get(_target, property) {
                    // A windowed read is answered in DISPLAY order from the collapsed view (see the
                    // module note); everything else routes as before.
                    if (TRANSLATED.has(property) && st.view !== null && st.order !== null) {
                        const view = st.view
                        const order = st.order
                        return (window: ViewWindow = {}) => fetchWindow(view, property as TranslatedMethod, window, order)
                    }
                    // The row COUNT is the display count: hidden subtotal rows are not drawn, and a
                    // consumer that measured the view itself would scroll past the end.
                    if (property === 'num_rows' && st.order !== null) {
                        const shown = st.order.M
                        return async () => shown
                    }
                    const target = (ROUTE_TO_REAL.has(property) ? real : (st.view ?? real)) as unknown as Record<PropertyKey, unknown>
                    const value = Reflect.get(target, property)
                    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(target) : value
                },
            }) as View
            m._view = st.proxy
        }

        const numRows = await collapsedView.num_rows()

        // The display order, once per (view, row count, hidden levels) — a group opened or closed
        // changes the count. A hidden field is a path LENGTH here: the subtotal rows of group_by[i]
        // are the paths of length i + 1, above the level or, once a group is opened, below it.
        // With no level at all — the grid as it opens, fully expanded — no total row is drawn, the
        // grand one included: the rows are the leaves, as a side-by-side grid always showed them, and
        // a group closed with [−] is its own total. A level brings the totals with it.
        const hiddenLengths = st.field === null
            ? new Set(Array.from({ length: groupBy.length + 1 }, (_, length) => length))
            : new Set(st.hiddenTotals.map(name => groupBy.indexOf(name) + 1).filter(length => length >= 1))
        const orderKey = JSON.stringify([depth, [...hiddenLengths].sort((a, b) => a - b)])
        if (st.order === null || st.orderView !== collapsedView || st.order.N !== numRows || st.orderKey !== orderKey) {
            await rebuildOrder(st, collapsedView, depth, hiddenLengths, orderKey)
        }
        const order = st.order as RowOrder
        // The display count, which is what the plugin's listener reports as num_rows.
        const count = order.M
        m._num_rows = count

        const res = await inner(x0, y0, x1, y1)

        // Rows are padded to the columns SHOWN — the collapse depth, plus any a group opened below
        // the level has added — never to group_by.length: the fields to the right are hidden, not
        // blank (`visibleRowFields`). The flat formatter has already formatted part i with the type
        // of group_by[i], and trimming keeps that mapping intact. Each row's kind comes from the
        // order: a subtotal is an ancestor whatever its length, and a leaf shorter than the columns
        // is a closed group with a blank tail.
        const first = Math.floor(y0)
        const rows = (res.row_headers ?? []).map((row, i) => collapsedRowHeader(row, order.shown, rowKindOf(order, first + i)))

        // Explicit: on the dimension call the plugin would otherwise take row_headers[0].length,
        // which for the TOTAL row's empty path is 0.
        return { ...res, row_headers: rows, num_rows: count, num_row_headers: order.shown }
    }
}
